import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Calendar, Users, Bike, DollarSign, Clock, CheckCircle, 
  XCircle, ChevronLeft, ChevronRight, LogOut,
  Filter, Plus, Send, CreditCard, X, FileText, Edit, Trash2,
  Bold, Italic, List, ListOrdered, Link2, Image, Quote, Heading2, Heading3,
  BarChart3, TrendingUp, TrendingDown, Monitor, Smartphone, Globe, MousePointer,
  CalendarOff, MapPin, Mountain, Shield, Mail, Star, ClipboardSignature, Copy, Download
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster, toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths } from "date-fns";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import ImageExtension from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";

// Resolve backend origin, defaulting to current window origin when env var is
// missing (happens on production builds where REACT_APP_BACKEND_URL is not baked in).
const RUNTIME_ORIGIN = (() => {
  const env = process.env.REACT_APP_BACKEND_URL;
  if (env && typeof env === "string" && env.startsWith("http")) return env.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location && window.location.origin) return window.location.origin;
  return "";
})();

// Utility to convert Dropbox URLs and resolve /api/media/* against backend
const convertDropboxUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${RUNTIME_ORIGIN}${url}`;
  if (url.startsWith('/images/')) return url; // static served by frontend
  if (url.includes('dropbox.com')) {
    let convertedUrl = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    convertedUrl = convertedUrl.replace(/[&?]dl=0/, '');
    return convertedUrl;
  }
  return url;
};

const BACKEND_URL = RUNTIME_ORIGIN;
const API = `${BACKEND_URL}/api`;

// Reusable image upload component for admin (chunked, requires authHeader prop)
const ImageUploadField = ({ value, onChange, label = "Image", testId = "image-upload", authHeader }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const CHUNK_SIZE = 256 * 1024;
  const MAX_SIZE_MB = 25;

  const uploadFile = async (file) => {
    setError("");
    setProgress(0);
    setUploading(true);
    try {
      const isImageMime = /^image\//i.test(file.type || "");
      const isImageExt = /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(file.name || "");
      if (!isImageMime && !isImageExt) {
        throw new Error("Endast bildfiler tillåtna (JPG, PNG, WebP, HEIC).");
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        throw new Error(`Filen är för stor: ${(file.size / (1024 * 1024)).toFixed(1)} MB (max ${MAX_SIZE_MB} MB).`);
      }
      const uploadId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      let finalUrl = null;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || "";
            const idx = result.indexOf(",");
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        let res;
        try {
          res = await axios.post(`${API}/admin/upload/media/chunk`, {
            upload_id: uploadId,
            chunk_index: i,
            total_chunks: totalChunks,
            file_name: file.name,
            mime_type: file.type || "image/jpeg",
            data: base64,
          }, {
            headers: authHeader ? { Authorization: authHeader } : {},
            timeout: 60000,
          });
        } catch (netErr) {
          const status = netErr?.response?.status;
          const detail =
            netErr?.response?.data?.detail ||
            netErr?.message ||
            "Okänt nätverksfel";
          throw new Error(`Chunk ${i + 1}/${totalChunks} misslyckades (HTTP ${status || "?"}): ${detail}`);
        }
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
        if (res.data?.url) finalUrl = res.data.url;
      }
      if (finalUrl) onChange(finalUrl);
      else throw new Error("Uppladdning slutfördes inte korrekt");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Uppladdning misslyckades";
      setError(typeof msg === "string" ? msg : "Uppladdning misslyckades");
      console.error("[ImageUpload] failure:", err);
    } finally {
      setUploading(false);
    }
  };

  const displaySrc = value ? (value.startsWith('http') ? convertDropboxUrl(value) : (value.startsWith('/api/') ? `${BACKEND_URL}${value}` : value)) : null;

  return (
    <div data-testid={testId}>
      <Label className="text-white/70 text-sm">{label}</Label>
      <div className="mt-1 space-y-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or upload below"
          className="bg-[#0A0A0A] border-white/10 text-white"
        />
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-3 py-1.5 text-sm font-manrope">
            <Image className="w-4 h-4" />
            {uploading ? `${progress}%` : "Ladda upp bild"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif"
              className="hidden"
              disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
              data-testid={`${testId}-file`}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-white/50 hover:text-red-400 text-sm underline"
              data-testid={`${testId}-clear`}
            >
              Ta bort
            </button>
          )}
        </div>
        {uploading && (
          <div className="h-1 bg-white/10 w-full">
            <div className="h-1 bg-[#EA580C] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        {error && <p className="text-red-400 text-xs">{error}</p>}
        {displaySrc && !uploading && (
          <img src={displaySrc} alt="Preview" className="mt-2 max-h-32 border border-white/10" />
        )}
      </div>
    </div>
  );
};

const PACKAGES = {
  half_day: { name: "10 Hour Rental", price: 249 },
  full_day: { name: "24 Hour Rental", price: 390 },
  guided_tour: { name: "Guided Full Day Tour", price: 690 },
  custom: { name: "Custom Booking", price: 0 }
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authHeader, setAuthHeader] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [blockBikes, setBlockBikes] = useState(1);
  const [blockReason, setBlockReason] = useState("");
  const [blockCountry, setBlockCountry] = useState("sweden");
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    package_id: "guided_tour",
    package_name: "",
    date: "",
    riders: 1,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    special_requests: "",
    custom_amount: "",
    send_payment_link: true,
    admin_notes: ""
  });
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [showArticleEditor, setShowArticleEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    meta_title: "",
    meta_description: "",
    tags: "",
    status: "draft",
    author: "Arctic Riders"
  });
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);

  // Blocked dates state
  const [blockedDates, setBlockedDates] = useState([]);
  const [showBlockDateForm, setShowBlockDateForm] = useState(false);
  const [blockDateForm, setBlockDateForm] = useState({
    country: "sweden", start_date: "", end_date: "", reason: ""
  });

  // Tour products state
  const [tourProducts, setTourProducts] = useState([]);  const [showTourEditor, setShowTourEditor] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [tourForm, setTourForm] = useState({
    title: "", title_sv: "", description: "", description_sv: "",
    image: "", gallery: [], date: "", end_date: "", time_start: "09:00", time_end: "17:00",
    price: "", max_riders: 4, difficulty: "medium", distance: "",
    route_description: "", route_description_sv: "",
    included: "", included_sv: "", excluded: "", excluded_sv: "",
    highlights: "", highlights_sv: "",
    meeting_point: "Torpvägen 20, 147 63 Uttran, Stockholm",
    meeting_point_sv: "Torpvägen 20, 147 63 Uttran, Stockholm",
    status: "draft"
  });

  // Rental agreements state
  const [agreements, setAgreements] = useState([]);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [createdAgreementLink, setCreatedAgreementLink] = useState(null);

  // Motorcycle fleet state
  const [motorcycles, setMotorcycles] = useState([]);
  const [showMotorcycleModal, setShowMotorcycleModal] = useState(false);
  const [editingMotorcycle, setEditingMotorcycle] = useState(null);
  const [mcForm, setMcForm] = useState({ registration: "", name: "", model: "", status: "active", notes: "" });
  const [mcSchedule, setMcSchedule] = useState(null); // { motorcycle, bookings, pending_agreements }

  const [agreementForm, setAgreementForm] = useState({
    package_id: "half_day",
    package_name: "",
    motorcycle_registration: "",
    date: "",
    end_date: "",
    time_slot: "",
    riders: 1,
    amount: "",
    language: "sv",
    prefill_email: "",
    admin_notes: "",
  });

  const login = async () => {
    const header = "Basic " + btoa(`${credentials.username}:${credentials.password}`);
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: header }
      });
      setAuthHeader(header);
      setIsAuthenticated(true);
      setDashboardStats(response.data);
      toast.success("Logged in successfully");
    } catch (e) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setAuthHeader("");
    setCredentials({ username: "", password: "" });
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: authHeader }
      });
      setDashboardStats(response.data);
    } catch (e) {
      console.error("Failed to fetch dashboard");
    }
  }, [authHeader]);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: authHeader }
      });
      setBookings(response.data.bookings);
    } catch (e) {
      console.error("Failed to fetch bookings");
    }
  }, [authHeader]);

  const fetchCalendar = useCallback(async () => {
    const start = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
    const end = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
    try {
      const response = await axios.get(`${API}/admin/calendar?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: authHeader }
      });
      setCalendarData(response.data.calendar);
    } catch (e) {
      console.error("Failed to fetch calendar");
    }
  }, [authHeader, calendarMonth]);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/articles`, {
        headers: { Authorization: authHeader }
      });
      setArticles(response.data.articles);
    } catch (e) {
      console.error("Failed to fetch articles");
    }
  }, [authHeader]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/overview?days=${analyticsPeriod}`, {
        headers: { Authorization: authHeader }
      });
      setAnalyticsData(response.data);
    } catch (e) {
      console.error("Failed to fetch analytics");
    }
  }, [authHeader, analyticsPeriod]);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blocked-dates`, {
        headers: { Authorization: authHeader }
      });
      setBlockedDates(response.data.blocked_dates || []);
    } catch (e) {
      console.error("Failed to fetch blocked dates");
    }
  }, [authHeader]);

  const fetchTourProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tour-products`, {
        headers: { Authorization: authHeader }
      });
      setTourProducts(response.data.tours || []);
    } catch (e) {
      console.error("Failed to fetch tour products");
    }
  }, [authHeader]);

  const fetchAgreements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/agreements`, {
        headers: { Authorization: authHeader }
      });
      setAgreements(response.data.agreements || []);
    } catch (e) {
      console.error("Failed to fetch agreements");
    }
  }, [authHeader]);

  const createAgreement = async () => {
    try {
      if (!agreementForm.date || !agreementForm.amount) {
        toast.error("Datum och belopp kravs");
        return;
      }
      const payload = {
        ...agreementForm,
        amount: parseFloat(agreementForm.amount),
        riders: parseInt(agreementForm.riders) || 1,
        time_slot: agreementForm.time_slot || null,
        end_date: agreementForm.end_date || null,
        prefill_email: agreementForm.prefill_email || null,
        package_id: agreementForm.package_id || null,
        motorcycle_registration: agreementForm.motorcycle_registration || null,
      };
      const response = await axios.post(`${API}/admin/agreements`, payload, {
        headers: { Authorization: authHeader }
      });
      const link = response.data.sign_url;
      setCreatedAgreementLink(link);
      toast.success("Hyresavtal skapat");
      fetchAgreements();
    } catch (e) {
      toast.error("Kunde inte skapa avtal");
    }
  };

  const deleteAgreement = async (id) => {
    if (!window.confirm("Ta bort detta avtal?")) return;
    try {
      await axios.delete(`${API}/admin/agreements/${id}`, {
        headers: { Authorization: authHeader }
      });
      toast.success("Avtal borttaget");
      fetchAgreements();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Lank kopierad");
  };

  const downloadAgreementPdf = async (id) => {
    try {
      const response = await axios.get(`${API}/admin/agreements/${id}/pdf`, {
        headers: { Authorization: authHeader },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `hyresavtal-${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Kunde inte ladda ner PDF");
    }
  };

  // ===== Motorcycle fleet =====
  const fetchMotorcycles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/motorcycles`, { headers: { Authorization: authHeader } });
      setMotorcycles(r.data.motorcycles || []);
    } catch (e) {
      console.error("Failed to fetch motorcycles");
    }
  }, [authHeader]);

  const saveMotorcycle = async () => {
    if (!mcForm.registration.trim()) {
      toast.error("Registreringsnummer kravs");
      return;
    }
    try {
      const payload = {
        registration: mcForm.registration.trim().toUpperCase(),
        name: mcForm.name || null,
        model: mcForm.model || null,
        status: mcForm.status || "active",
        notes: mcForm.notes || null,
      };
      if (editingMotorcycle) {
        await axios.patch(`${API}/admin/motorcycles/${editingMotorcycle.id}`, payload,
          { headers: { Authorization: authHeader } });
        toast.success("MC uppdaterad");
      } else {
        await axios.post(`${API}/admin/motorcycles`, payload,
          { headers: { Authorization: authHeader } });
        toast.success("MC tillagd");
      }
      setShowMotorcycleModal(false);
      setEditingMotorcycle(null);
      setMcForm({ registration: "", name: "", model: "", status: "active", notes: "" });
      fetchMotorcycles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara MC");
    }
  };

  const deleteMotorcycle = async (id) => {
    if (!window.confirm("Ta bort denna MC?")) return;
    try {
      await axios.delete(`${API}/admin/motorcycles/${id}`, { headers: { Authorization: authHeader } });
      toast.success("MC borttagen");
      fetchMotorcycles();
      if (mcSchedule?.motorcycle?.id === id) setMcSchedule(null);
    } catch (e) {
      toast.error("Kunde inte ta bort");
    }
  };

  const openEditMotorcycle = (mc) => {
    setEditingMotorcycle(mc);
    setMcForm({
      registration: mc.registration || "",
      name: mc.name || "",
      model: mc.model || "",
      status: mc.status || "active",
      notes: mc.notes || "",
    });
    setShowMotorcycleModal(true);
  };

  const openNewMotorcycle = () => {
    setEditingMotorcycle(null);
    setMcForm({ registration: "", name: "", model: "", status: "active", notes: "" });
    setShowMotorcycleModal(true);
  };

  const loadMcSchedule = async (mc) => {
    try {
      const r = await axios.get(`${API}/admin/motorcycles/${mc.id}/schedule`,
        { headers: { Authorization: authHeader } });
      setMcSchedule(r.data);
    } catch (e) {
      toast.error("Kunde inte hamta schema");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === "dashboard") fetchDashboard();
      if (activeTab === "bookings") fetchBookings();
      if (activeTab === "calendar") fetchCalendar();
      if (activeTab === "articles") fetchArticles();
      if (activeTab === "analytics") fetchAnalytics();
      if (activeTab === "tours") fetchTourProducts();
      if (activeTab === "agreements") fetchAgreements();
      if (activeTab === "fleet") fetchMotorcycles();
    }
  }, [isAuthenticated, activeTab, fetchDashboard, fetchBookings, fetchCalendar, fetchArticles, fetchAnalytics, fetchTourProducts, fetchAgreements, fetchMotorcycles]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status }, {
        headers: { Authorization: authHeader }
      });
      toast.success("Booking updated");
      fetchBookings();
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to update booking");
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success("Booking cancelled");
      fetchBookings();
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to cancel booking");
    }
  };

  const resendPaymentLink = async (bookingId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/resend-payment-link`, {}, {
        headers: { Authorization: authHeader }
      });
      if (response.data.email_sent) {
        toast.success("Payment link sent to customer");
      } else {
        toast.success("Payment link created");
      }
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend payment link");
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (bookingId) => {
    if (!window.confirm("Mark this booking as paid? A confirmation email will be sent to the customer.")) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success("Booking marked as paid");
      fetchBookings();
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to mark as paid");
    }
  };

  const downloadReceipt = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/receipt`, {
        headers: { Authorization: authHeader },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${bookingId.slice(0,8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded");
    } catch (e) {
      toast.error("Failed to download receipt");
    }
  };

  const sendReceiptEmail = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/send-receipt`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success("Receipt email sent to customer");
    } catch (e) {
      toast.error("Failed to send receipt");
    }
  };

  const resendConfirmation = async (bookingId) => {
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/resend-confirmation`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success(res.data.message || "Confirmation + receipt sent");
    } catch (e) {
      toast.error("Failed to resend");
    }
  };

  const sendReviewRequest = async (bookingId) => {
    if (!window.confirm("Send a Google review request email to this customer?")) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/send-review-request`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success("Review request email sent!");
      fetchBookings();
    } catch (e) {
      toast.error("Failed to send review request");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to PERMANENTLY DELETE this booking? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success("Booking deleted");
      fetchBookings();
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to delete booking");
    }
  };

  const createManualBooking = async () => {
    if (!newBooking.customer_name || !newBooking.customer_email || !newBooking.date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...newBooking,
        custom_amount: newBooking.custom_amount ? parseFloat(newBooking.custom_amount) : null
      };
      
      const response = await axios.post(`${API}/admin/bookings/create`, payload, {
        headers: { Authorization: authHeader }
      });
      
      toast.success(response.data.message);
      setShowNewBooking(false);
      setNewBooking({
        package_id: "guided_tour",
        package_name: "",
        date: "",
        riders: 1,
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        special_requests: "",
        custom_amount: "",
        send_payment_link: true,
        admin_notes: ""
      });
      fetchBookings();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  // Article functions
  const openArticleEditor = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        content: article.content,
        featured_image: article.featured_image || "",
        meta_title: article.meta_title || "",
        meta_description: article.meta_description || "",
        tags: article.tags?.join(", ") || "",
        status: article.status,
        author: article.author
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        featured_image: "",
        meta_title: "",
        meta_description: "",
        tags: "",
        status: "draft",
        author: "Arctic Riders"
      });
    }
    setShowArticleEditor(true);
  };

  const saveArticle = async () => {
    if (!articleForm.title || !articleForm.excerpt) {
      toast.error("Please fill in title and excerpt");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...articleForm,
        tags: articleForm.tags ? articleForm.tags.split(",").map(t => t.trim()).filter(t => t) : []
      };

      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, payload, {
          headers: { Authorization: authHeader }
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, payload, {
          headers: { Authorization: authHeader }
        });
        toast.success("Article created");
      }
      
      setShowArticleEditor(false);
      fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save article");
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success("Article deleted");
      fetchArticles();
    } catch (e) {
      toast.error("Failed to delete article");
    }
  };

  const toggleArticleStatus = async (article) => {
    const newStatus = article.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/admin/articles/${article.id}`, { status: newStatus }, {
        headers: { Authorization: authHeader }
      });
      toast.success(`Article ${newStatus === "published" ? "published" : "unpublished"}`);
      fetchArticles();
    } catch (e) {
      toast.error("Failed to update article status");
    }
  };

  const getPaymentStatusBadge = (booking) => {
    const isPaid = booking.payment_status === "paid" || booking.status === "confirmed";
    const isAwaiting = booking.status === "awaiting_payment" || booking.status === "pending";
    
    if (isPaid) {
      return <span className="px-2 py-1 text-xs uppercase tracking-wider bg-green-500/20 text-green-400">Paid</span>;
    } else if (isAwaiting) {
      return <span className="px-2 py-1 text-xs uppercase tracking-wider bg-yellow-500/20 text-yellow-400">Unpaid</span>;
    } else {
      return <span className="px-2 py-1 text-xs uppercase tracking-wider bg-red-500/20 text-red-400">{booking.status}</span>;
    }
  };

  const filteredBookings = bookings.filter(b => 
    statusFilter === "all" || b.status === statusFilter || 
    (statusFilter === "unpaid" && (b.status === "awaiting_payment" || b.status === "pending")) ||
    (statusFilter === "paid" && (b.payment_status === "paid" || b.status === "confirmed"))
  );

  // Calculate suggested price
  const getSuggestedPrice = () => {
    const pkg = PACKAGES[newBooking.package_id];
    if (pkg && newBooking.package_id !== "custom") {
      return pkg.price * newBooking.riders;
    }
    return 0;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="top-center" theme="dark" />
        <div className="bg-[#171717] border border-white/10 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-anton text-3xl text-white mb-2">ARCTIC RIDERS</h1>
            <p className="text-white/60 font-manrope">Admin Panel</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-white/70 text-sm mb-2 block">Username</label>
              <Input
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="admin"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm mb-2 block">Password</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="••••••••"
                onKeyDown={(e) => e.key === "Enter" && login()}
                data-testid="admin-password"
              />
            </div>
            <Button 
              onClick={login} 
              disabled={loading}
              className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white"
              data-testid="admin-login"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard Stats Card
  const StatCard = ({ icon: Icon, label, value, subvalue, color = "orange" }) => (
    <div className="bg-[#171717] border border-white/5 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 flex items-center justify-center ${color === "orange" ? "bg-[#EA580C]/20" : "bg-[#2e3104]"}`}>
          <Icon className={`w-5 h-5 ${color === "orange" ? "text-[#EA580C]" : "text-[#4A4F06]"}`} />
        </div>
      </div>
      <div className="font-anton text-3xl text-white mb-1">{value}</div>
      <div className="text-white/60 text-sm font-manrope">{label}</div>
      {subvalue && <div className="text-white/40 text-xs mt-1">{subvalue}</div>}
    </div>
  );

  // Calendar Day Cell
  const CalendarDay = ({ date }) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dayData = calendarData[dateStr];
    const isToday = isSameDay(date, new Date());
    const bikesBooked = dayData?.bikes_booked || 0;
    const bikesBlocked = dayData?.bikes_blocked || 0;
    const bookings = dayData?.bookings || [];
    const blockedEntries = dayData?.blocked_entries || [];
    const totalUnavailable = Math.min(bikesBooked + bikesBlocked, 4);
    const available = 4 - totalUnavailable;
    const isPast = date < new Date(new Date().setHours(0,0,0,0));
    
    return (
      <div 
        className={`p-1.5 sm:p-2 border border-white/5 min-h-[70px] sm:min-h-[90px] cursor-pointer hover:border-[#EA580C]/30 transition-colors ${
          isToday ? "bg-[#2e3104]/30 border-[#EA580C]/20" : ""
        } ${isPast ? "opacity-50" : ""}`}
        onClick={() => {
          setSelectedCalendarDate(dateStr);
          setSelectedDayData(dayData);
          setShowDayModal(true);
          setShowBlockForm(false);
          setSelectedBooking(null);
          const avail = Math.max(0, 4 - (dayData?.bikes_booked || 0) - (dayData?.bikes_blocked || 0));
          setBlockBikes(avail > 0 ? Math.min(avail, 4) : 1);
          setBlockReason("");
          setBlockCountry("sweden");
        }}
        data-testid={`cal-day-${dateStr}`}
      >
        <div className={`text-xs sm:text-sm mb-1 ${isToday ? "text-[#EA580C] font-bold" : "text-white/60"}`}>
          {format(date, "d")}
        </div>
        
        {/* Bike availability dots */}
        <div className="flex gap-0.5 mb-1">
          {[0,1,2,3].map(i => (
            <div key={i} className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${
              i < bikesBooked ? "bg-[#EA580C]" : 
              i < bikesBooked + bikesBlocked ? "bg-red-500" : 
              "bg-green-500/40"
            }`} title={i < bikesBooked ? "Booked" : i < bikesBooked + bikesBlocked ? "Blocked" : "Available"} />
          ))}
        </div>
        
        {/* Booking indicators */}
        {bookings.length > 0 && (
          <div className="text-[9px] sm:text-[10px] text-[#EA580C] font-manrope truncate">
            {bookings.length} booking{bookings.length > 1 ? "s" : ""}
          </div>
        )}
        {blockedEntries.length > 0 && bookings.length === 0 && (
          <div className="text-[9px] sm:text-[10px] text-red-400 font-manrope truncate">
            {bikesBlocked} blocked
          </div>
        )}
      </div>
    );
  };

  // Day Detail Modal
  const dayDetailModalJSX = (showDayModal && selectedCalendarDate) ? (() => {
    const dayData = selectedDayData || { bookings: [], bikes_booked: 0, bikes_blocked: 0, blocked_entries: [] };
    const bookings = dayData.bookings || [];
    const blockedEntries = dayData.blocked_entries || [];
    const bikesBooked = dayData.bikes_booked || 0;
    const bikesBlocked = dayData.bikes_blocked || 0;
    const available = Math.max(0, 4 - bikesBooked - bikesBlocked);

    const dateObj = parseISO(selectedCalendarDate);
    const dateLabel = format(dateObj, "EEEE, MMMM d, yyyy");

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="day-detail-modal">
        <div className="absolute inset-0 bg-black/80" onClick={() => { setShowDayModal(false); setSelectedBooking(null); }} />
        <div className="relative bg-[#171717] border border-white/10 w-full max-w-xl max-h-[85vh] overflow-y-auto">
          <div className="sticky top-0 bg-[#171717] border-b border-white/10 p-4 flex items-center justify-between z-10">
            <div>
              <h3 className="font-anton text-lg text-white">{dateLabel}</h3>
              <div className="flex gap-3 mt-1">
                <span className="text-green-400 text-xs font-manrope">{available} available</span>
                {bikesBooked > 0 && <span className="text-[#EA580C] text-xs font-manrope">{bikesBooked} booked</span>}
                {bikesBlocked > 0 && <span className="text-red-400 text-xs font-manrope">{bikesBlocked} blocked</span>}
              </div>
            </div>
            <button onClick={() => { setShowDayModal(false); setSelectedBooking(null); }} className="text-white/50 hover:text-white"><X size={20} /></button>
          </div>

          <div className="p-4 space-y-4">
            {/* Bike visual */}
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs font-manrope w-16">Bikes:</span>
              <div className="flex gap-1.5">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded ${
                    i < bikesBooked ? "bg-[#EA580C] text-white" : 
                    i < bikesBooked + bikesBlocked ? "bg-red-500/80 text-white" : 
                    "bg-green-500/20 text-green-400 border border-green-500/30"
                  }`}>
                    {i < bikesBooked ? "B" : i < bikesBooked + bikesBlocked ? "X" : i + 1}
                  </div>
                ))}
              </div>
            </div>

            {/* Bookings list */}
            {bookings.length > 0 && (
              <div>
                <h4 className="font-anton text-sm text-white mb-2 uppercase tracking-wider">Bookings ({bookings.length})</h4>
                <div className="space-y-2">
                  {bookings.map((b) => (
                    <div key={b.id} className={`bg-[#0A0A0A] border border-white/5 p-3 cursor-pointer hover:border-[#EA580C]/30 transition-colors ${selectedBooking?.id === b.id ? 'border-[#EA580C]/50' : ''}`}
                      onClick={() => setSelectedBooking(selectedBooking?.id === b.id ? null : b)}
                      data-testid={`cal-booking-${b.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-white text-sm font-semibold">{b.customer_name}</span>
                          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase font-bold ${
                            b.country === 'sweden' ? 'bg-blue-500/20 text-blue-400' : 'bg-cyan-500/20 text-cyan-400'
                          }`}>{b.country || 'iceland'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#EA580C] text-xs">{b.riders} rider{b.riders > 1 ? 's' : ''}</span>
                          <span className={`px-1.5 py-0.5 text-[10px] uppercase font-bold ${
                            b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 
                            b.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 
                            'bg-orange-500/20 text-orange-400'
                          }`}>{b.status}</span>
                        </div>
                      </div>
                      
                      {/* Expanded booking details */}
                      {selectedBooking?.id === b.id && (
                        <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 text-xs font-manrope">
                          <div className="flex justify-between"><span className="text-white/50">Package</span><span className="text-white">{b.package_name || b.package_id}</span></div>
                          <div className="flex justify-between"><span className="text-white/50">Email</span><span className="text-white">{b.customer_email}</span></div>
                          <div className="flex justify-between"><span className="text-white/50">Phone</span><span className="text-white">{b.customer_phone}</span></div>
                          <div className="flex justify-between"><span className="text-white/50">Total</span><span className="text-[#EA580C] font-bold">{b.currency === 'sek' ? `${Number(b.total_price).toLocaleString('sv-SE')} kr` : `€${b.total_price}`}</span></div>
                          <div className="flex justify-between"><span className="text-white/50">Payment</span><span className={b.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}>{b.payment_status}</span></div>
                          {b.special_requests && <div><span className="text-white/50">Notes: </span><span className="text-white/70">{b.special_requests}</span></div>}
                          {b.admin_notes && <div><span className="text-white/50">Admin: </span><span className="text-white/70">{b.admin_notes}</span></div>}
                          {(b.payment_status === "paid" || b.status === "confirmed") && (
                            <div className="flex gap-2 pt-2 mt-2 border-t border-white/10">
                              <button onClick={() => downloadReceipt(b.id)} className="text-[#EA580C] hover:text-[#C2410C] text-xs flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Download PDF
                              </button>
                              <button onClick={() => resendConfirmation(b.id)} className="text-purple-400 hover:text-purple-300 text-xs flex items-center gap-1">
                                <Mail className="w-3 h-3" /> Resend All Emails
                              </button>
                              <button onClick={() => sendReviewRequest(b.id)} className="text-yellow-400 hover:text-yellow-300 text-xs flex items-center gap-1">
                                <Star className="w-3 h-3" /> Review Request
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blocked entries */}
            {blockedEntries.length > 0 && (
              <div>
                <h4 className="font-anton text-sm text-white mb-2 uppercase tracking-wider">Blocked ({bikesBlocked} bikes)</h4>
                <div className="space-y-2">
                  {blockedEntries.map((be, i) => (
                    <div key={be.id || i} className="bg-red-500/10 border border-red-500/20 p-3 flex items-center justify-between">
                      <div>
                        <span className="text-red-400 text-sm">{be.bikes_blocked} bike{be.bikes_blocked > 1 ? 's' : ''} blocked</span>
                        {be.reason && <span className="text-white/50 text-xs ml-2">— {be.reason}</span>}
                        <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase font-bold ${
                          be.country === 'sweden' ? 'bg-blue-500/20 text-blue-400' : be.country === 'iceland' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'
                        }`}>{be.country}</span>
                      </div>
                      <button onClick={async () => {
                        if (!window.confirm("Remove this block?")) return;
                        try {
                          await axios.delete(`${API}/admin/blocked-dates/${be.id}`, { headers: { Authorization: authHeader } });
                          toast.success("Block removed");
                          fetchCalendar();
                          // Refresh modal data
                          const res = await axios.get(`${API}/admin/calendar?start_date=${selectedCalendarDate}&end_date=${selectedCalendarDate}`, { headers: { Authorization: authHeader } });
                          setSelectedDayData(res.data.calendar[selectedCalendarDate] || null);
                        } catch (e) { toast.error("Failed"); }
                      }} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Block bikes form */}
            {!showBlockForm ? (
              <Button onClick={() => setShowBlockForm(true)} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="block-bikes-btn">
                <CalendarOff className="w-4 h-4 mr-2" /> Block Bikes on This Day
              </Button>
            ) : (
              <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-3">
                <h4 className="font-anton text-sm text-white">Block Bikes</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-white/70 text-[11px]">Bikes to block</Label>
                    <div className="flex gap-1 mt-1">
                      {[1,2,3,4].map(n => (
                        <button key={n} onClick={() => setBlockBikes(n)}
                          className={`w-9 h-9 text-sm font-bold ${blockBikes === n ? 'bg-red-500 text-white' : 'bg-[#171717] border border-white/10 text-white/60 hover:text-white'}`}
                          data-testid={`block-${n}-bikes`}
                        >{n}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/70 text-[11px]">Country</Label>
                    <Select value={blockCountry} onValueChange={setBlockCountry}>
                      <SelectTrigger className="bg-[#171717] border-white/10 text-white mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-white/10">
                        <SelectItem value="sweden">Sweden</SelectItem>
                        <SelectItem value="iceland">Iceland</SelectItem>
                        <SelectItem value="all">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70 text-[11px]">Reason</Label>
                    <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Optional" className="bg-[#171717] border-white/10 text-white mt-1 h-9 text-xs" data-testid="block-reason-input" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/blocked-dates`, {
                        country: blockCountry, start_date: selectedCalendarDate, end_date: selectedCalendarDate,
                        reason: blockReason, bikes_blocked: blockBikes
                      }, { headers: { Authorization: authHeader } });
                      toast.success(`${blockBikes} bike${blockBikes > 1 ? 's' : ''} blocked`);
                      setShowBlockForm(false);
                      fetchCalendar();
                      const res = await axios.get(`${API}/admin/calendar?start_date=${selectedCalendarDate}&end_date=${selectedCalendarDate}`, { headers: { Authorization: authHeader } });
                      setSelectedDayData(res.data.calendar[selectedCalendarDate] || null);
                    } catch (e) { toast.error("Failed to block"); }
                  }} className="bg-red-500 hover:bg-red-600 text-white text-xs" data-testid="confirm-block-btn">Block {blockBikes} Bike{blockBikes > 1 ? 's' : ''}</Button>
                  <Button variant="outline" onClick={() => setShowBlockForm(false)} className="border-white/10 text-white text-xs">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  })() : null;

  // New Booking Modal
  const newBookingModalJSX = showNewBooking ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={() => setShowNewBooking(false)} />
      <div className="relative bg-[#171717] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#171717] border-b border-white/10 p-4 flex items-center justify-between">
          <h3 className="font-anton text-xl text-white">Create Manual Booking</h3>
          <button onClick={() => setShowNewBooking(false)} className="text-white/50 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-white/70 text-sm">Package</Label>
            <Select value={newBooking.package_id} onValueChange={(v) => setNewBooking({...newBooking, package_id: v})}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-white/10">
                <SelectItem value="guided_tour">Guided Full Day Tour (€690)</SelectItem>
                <SelectItem value="full_day">24 Hour Rental (€390)</SelectItem>
                <SelectItem value="half_day">10 Hour Rental (€249)</SelectItem>
                <SelectItem value="custom">Custom Booking</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {newBooking.package_id === "custom" && (
            <div>
              <Label className="text-white/70 text-sm">Custom Package Name</Label>
              <Input
                value={newBooking.package_name}
                onChange={(e) => setNewBooking({...newBooking, package_name: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="e.g., 2-Day Adventure Package"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-sm">Date *</Label>
              <Input
                type="date"
                value={newBooking.date}
                onChange={(e) => setNewBooking({...newBooking, date: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Riders</Label>
              <Input
                type="number"
                min="1"
                max="6"
                value={newBooking.riders}
                onChange={(e) => setNewBooking({...newBooking, riders: parseInt(e.target.value) || 1})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/70 text-sm">Customer Name *</Label>
            <Input
              value={newBooking.customer_name}
              onChange={(e) => setNewBooking({...newBooking, customer_name: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder="Full name"
            />
          </div>

          <div>
            <Label className="text-white/70 text-sm">Customer Email *</Label>
            <Input
              type="email"
              value={newBooking.customer_email}
              onChange={(e) => setNewBooking({...newBooking, customer_email: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <Label className="text-white/70 text-sm">Customer Phone</Label>
            <Input
              value={newBooking.customer_phone}
              onChange={(e) => setNewBooking({...newBooking, customer_phone: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder="+354..."
            />
          </div>

          <div>
            <Label className="text-white/70 text-sm">
              Custom Amount (€) 
              <span className="text-white/40 ml-2">
                Suggested: €{getSuggestedPrice()}
              </span>
            </Label>
            <Input
              type="number"
              value={newBooking.custom_amount}
              onChange={(e) => setNewBooking({...newBooking, custom_amount: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder={`Leave empty for €${getSuggestedPrice()}`}
            />
            <p className="text-white/40 text-xs mt-1">Use this to apply discounts or special pricing</p>
          </div>

          <div>
            <Label className="text-white/70 text-sm">Admin Notes</Label>
            <textarea
              value={newBooking.admin_notes}
              onChange={(e) => setNewBooking({...newBooking, admin_notes: e.target.value})}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white p-2 mt-1 min-h-[80px]"
              placeholder="Internal notes (not visible to customer)"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={newBooking.send_payment_link}
              onChange={(e) => setNewBooking({...newBooking, send_payment_link: e.target.checked})}
              className="w-4 h-4 accent-[#EA580C]"
            />
            <span className="text-white/70 text-sm">Send payment link to customer</span>
          </div>

          <div className="pt-4 border-t border-white/10 flex gap-3">
            <Button
              onClick={() => setShowNewBooking(false)}
              variant="outline"
              className="flex-1 border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={createManualBooking}
              disabled={loading}
              className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white"
            >
              {loading ? "Creating..." : "Create Booking"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // TipTap Rich Text Editor Component
  const RichTextEditor = ({ content, onChange }) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3]
          }
        }),
        LinkExtension.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-[#EA580C] underline'
          }
        }),
        ImageExtension.configure({
          HTMLAttributes: {
            class: 'max-w-full rounded'
          }
        }),
        Placeholder.configure({
          placeholder: 'Start writing your article content here...'
        })
      ],
      content: content,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      }
    });

    if (!editor) return null;

    const addLink = () => {
      const url = window.prompt('Enter URL:');
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    };

    const addImage = () => {
      const url = window.prompt('Enter image URL:');
      if (!url) return;
      
      const size = window.prompt('Image size?\n1 = Small (33%)\n2 = Medium (50%)\n3 = Large (75%)\n4 = Full width (100%)\n\nEnter 1, 2, 3, or 4:', '4');
      
      const sizeClass = {
        '1': 'img-small',
        '2': 'img-medium', 
        '3': 'img-large',
        '4': 'img-full'
      }[size] || 'img-full';
      
      // Insert image with size class
      editor.chain().focus().insertContent(`<img src="${url}" class="${sizeClass}" />`).run();
    };

    const addGallery = () => {
      const count = window.prompt('How many images? (2 or 3):', '2');
      if (!['2', '3'].includes(count)) {
        alert('Please enter 2 or 3');
        return;
      }
      
      const urls = [];
      for (let i = 1; i <= parseInt(count); i++) {
        const url = window.prompt(`Enter URL for image ${i}:`);
        if (!url) return;
        urls.push(url);
      }
      
      const galleryClass = count === '2' ? 'img-gallery-2' : 'img-gallery-3';
      const imagesHtml = urls.map(url => `<img src="${url}" />`).join('');
      
      editor.chain().focus().insertContent(`<div class="${galleryClass}">${imagesHtml}</div>`).run();
    };

    return (
      <div className="border border-white/10 rounded">
        <div className="flex flex-wrap gap-1 p-2 border-b border-white/10 bg-[#0A0A0A]">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('bold') ? 'bg-white/20' : ''}`}
            title="Bold"
          >
            <Bold className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('italic') ? 'bg-white/20' : ''}`}
            title="Italic"
          >
            <Italic className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('heading', { level: 2 }) ? 'bg-white/20' : ''}`}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('heading', { level: 3 }) ? 'bg-white/20' : ''}`}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('bulletList') ? 'bg-white/20' : ''}`}
            title="Bullet List"
          >
            <List className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('orderedList') ? 'bg-white/20' : ''}`}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('blockquote') ? 'bg-white/20' : ''}`}
            title="Quote"
          >
            <Quote className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={addLink}
            className={`p-2 hover:bg-white/10 rounded ${editor.isActive('link') ? 'bg-white/20' : ''}`}
            title="Add Link"
          >
            <Link2 className="w-4 h-4 text-white" />
          </button>
          
          {/* Separator */}
          <div className="w-px h-8 bg-white/20 mx-1 self-center" />
          
          <button
            type="button"
            onClick={addImage}
            className="p-2 hover:bg-white/10 rounded"
            title="Add Single Image (with size options)"
          >
            <Image className="w-4 h-4 text-white" />
          </button>
          <button
            type="button"
            onClick={addGallery}
            className="p-2 hover:bg-white/10 rounded flex items-center gap-1"
            title="Add Image Gallery (2-3 images side by side)"
          >
            <Image className="w-4 h-4 text-white" />
            <span className="text-white text-xs">2-3</span>
          </button>
        </div>
        <EditorContent 
          editor={editor} 
          className="prose prose-invert max-w-none p-4 min-h-[300px] text-white focus:outline-none [&_.ProseMirror]:min-h-[280px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-white/30 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
        />
      </div>
    );
  };

  // Article Editor Modal
  const articleEditorModalJSX = showArticleEditor ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={() => setShowArticleEditor(false)} />
      <div className="relative bg-[#171717] border border-white/10 w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#171717] border-b border-white/10 p-4 flex items-center justify-between z-10">
          <h3 className="font-anton text-xl text-white">
            {editingArticle ? "Edit Article" : "Create New Article"}
          </h3>
          <button onClick={() => setShowArticleEditor(false)} className="text-white/50 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-sm">Title *</Label>
              <Input
                value={articleForm.title}
                onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="Article title"
                data-testid="article-title"
              />
            </div>
            <div>
              <Label className="text-white/70 text-sm">URL Slug</Label>
              <Input
                value={articleForm.slug}
                onChange={(e) => setArticleForm({...articleForm, slug: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="auto-generated-from-title"
                data-testid="article-slug"
              />
              <p className="text-white/40 text-xs mt-1">Leave empty to auto-generate from title</p>
            </div>
          </div>

          <div>
            <Label className="text-white/70 text-sm">Excerpt *</Label>
            <textarea
              value={articleForm.excerpt}
              onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 min-h-[80px]"
              placeholder="A brief summary of the article (shown in article list)"
              data-testid="article-excerpt"
            />
          </div>

          <div>
            <Label className="text-white/70 text-sm mb-2 block">Content *</Label>
            <RichTextEditor 
              key={editingArticle?.id || 'new-article'}
              content={articleForm.content}
              onChange={(html) => setArticleForm({...articleForm, content: html})}
            />
          </div>

          <ImageUploadField
            value={articleForm.featured_image}
            onChange={(v) => setArticleForm({...articleForm, featured_image: v})}
            label="Featured Image"
            testId="article-image"
            authHeader={authHeader}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-sm">Tags</Label>
              <Input
                value={articleForm.tags}
                onChange={(e) => setArticleForm({...articleForm, tags: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="iceland, motorcycle, adventure"
                data-testid="article-tags"
              />
              <p className="text-white/40 text-xs mt-1">Comma-separated tags</p>
            </div>
            <div>
              <Label className="text-white/70 text-sm">Author</Label>
              <Input
                value={articleForm.author}
                onChange={(e) => setArticleForm({...articleForm, author: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="Arctic Riders"
              />
            </div>
          </div>

          {/* SEO Section */}
          <div className="border-t border-white/10 pt-6">
            <h4 className="font-anton text-lg text-white mb-4">SEO Settings</h4>
            <div className="space-y-4">
              <div>
                <Label className="text-white/70 text-sm">Meta Title</Label>
                <Input
                  value={articleForm.meta_title}
                  onChange={(e) => setArticleForm({...articleForm, meta_title: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="Leave empty to use article title"
                />
              </div>
              <div>
                <Label className="text-white/70 text-sm">Meta Description</Label>
                <textarea
                  value={articleForm.meta_description}
                  onChange={(e) => setArticleForm({...articleForm, meta_description: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 min-h-[60px]"
                  placeholder="Leave empty to use excerpt (max 160 chars)"
                  maxLength={160}
                />
                <p className="text-white/40 text-xs mt-1">
                  {articleForm.meta_description.length}/160 characters
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Label className="text-white/70 text-sm">Status</Label>
            <Select 
              value={articleForm.status} 
              onValueChange={(v) => setArticleForm({...articleForm, status: v})}
            >
              <SelectTrigger className="w-40 bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-white/10">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="pt-4 border-t border-white/10 flex gap-3">
            <Button
              onClick={() => setShowArticleEditor(false)}
              variant="outline"
              className="flex-1 border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={saveArticle}
              disabled={loading}
              className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white"
              data-testid="save-article-btn"
            >
              {loading ? "Saving..." : (editingArticle ? "Update Article" : "Create Article")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster position="top-center" theme="dark" />
      
      {newBookingModalJSX}
      {articleEditorModalJSX}
      {dayDetailModalJSX}
      
      {/* Header */}
      <header className="bg-[#171717] border-b border-white/10 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <h1 className="font-anton text-lg text-white whitespace-nowrap">ARCTIC RIDERS</h1>
          <nav className="flex gap-0.5 overflow-x-auto flex-1 mx-2">
              {[
                { id: "dashboard", label: "Dashboard", icon: DollarSign },
                { id: "bookings", label: "Bookings", icon: Calendar },
                { id: "calendar", label: "Calendar", icon: Calendar },
                { id: "tours", label: "Tours", icon: Mountain },
                { id: "fleet", label: "MC-flotta", icon: Bike },
                { id: "agreements", label: "Avtal", icon: ClipboardSignature },
                { id: "articles", label: "Articles", icon: FileText },
                { id: "analytics", label: "Analytics", icon: BarChart3 }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-xs font-manrope flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                    activeTab === tab.id 
                      ? "bg-[#EA580C] text-white" 
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button 
              onClick={() => setShowNewBooking(true)}
              className="flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 text-sm"
              data-testid="new-booking-btn"
            >
              <Plus className="w-4 h-4" />
              New Booking
            </button>
            <button 
              onClick={logout}
              className="flex items-center gap-2 text-white/60 hover:text-white text-sm"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && dashboardStats && (
          <div className="space-y-6" data-testid="dashboard-view">
            <h2 className="font-anton text-2xl text-white">Dashboard Overview</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Calendar} label="Total Bookings" value={dashboardStats.total_bookings} />
              <StatCard icon={CheckCircle} label="Confirmed" value={dashboardStats.confirmed_bookings} color="green" />
              <StatCard icon={Clock} label="Pending" value={dashboardStats.pending_bookings} />
              <StatCard icon={DollarSign} label="Revenue" value={`€${dashboardStats.total_revenue.toLocaleString()}`} />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#171717] border border-white/5 p-6">
                <h3 className="font-anton text-xl text-white mb-4">Today's Status</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Bookings Today</span>
                    <span className="font-anton text-xl text-white">{dashboardStats.today_bookings}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Bikes Booked</span>
                    <span className="font-anton text-xl text-[#EA580C]">{dashboardStats.bikes_booked_today}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Bikes Available</span>
                    <span className="font-anton text-xl text-green-500">{dashboardStats.bikes_available_today}</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#171717] border border-white/5 p-6">
                <h3 className="font-anton text-xl text-white mb-4">Fleet Status</h3>
                <div className="flex items-center gap-4">
                  {[...Array(dashboardStats.total_fleet)].map((_, i) => (
                    <div 
                      key={i}
                      className={`w-12 h-12 flex items-center justify-center ${
                        i < dashboardStats.bikes_booked_today ? "bg-[#EA580C]/20" : "bg-[#2e3104]"
                      }`}
                    >
                      <Bike className={`w-6 h-6 ${
                        i < dashboardStats.bikes_booked_today ? "text-[#EA580C]" : "text-[#4A4F06]"
                      }`} />
                    </div>
                  ))}
                </div>
                <p className="text-white/40 text-sm mt-4">
                  {dashboardStats.bikes_booked_today} of {dashboardStats.total_fleet} bikes booked today
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-6" data-testid="bookings-view">
            <div className="flex items-center justify-between">
              <h2 className="font-anton text-2xl text-white">All Bookings</h2>
              <div className="flex items-center gap-4">
                <Filter className="w-4 h-4 text-white/40" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 bg-[#171717] border-white/10 text-white">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-white/10">
                    <SelectItem value="all">All Bookings</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-[#171717] border border-white/5 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#0A0A0A]">
                  <tr>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Country</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Date</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Customer</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Package</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Riders</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Total</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Payment</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => {
                    const isSweden = booking.country === 'sweden';
                    const currencySymbol = isSweden ? 'kr' : '€';
                    const countryBadgeClass = isSweden 
                      ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      : 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
                    return (
                    <tr key={booking.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider border ${countryBadgeClass}`}>
                          {isSweden ? '🇸🇪 SE' : '🇮🇸 IS'}
                        </span>
                      </td>
                      <td className="p-4 text-white font-manrope">
                        {booking.date ? format(parseISO(booking.date), "MMM d, yyyy") : "-"}
                      </td>
                      <td className="p-4">
                        <div className="text-white">{booking.customer_name}</div>
                        <div className="text-white/50 text-sm">{booking.customer_email}</div>
                      </td>
                      <td className="p-4 text-white">{booking.package_name}</td>
                      <td className="p-4 text-white">{booking.riders}</td>
                      <td className="p-4 text-[#EA580C] font-anton">{currencySymbol}{booking.total_price?.toLocaleString()}</td>
                      <td className="p-4">
                        {getPaymentStatusBadge(booking)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {(booking.status === "awaiting_payment" || booking.status === "pending") && booking.payment_status !== "paid" && (
                            <>
                              <button 
                                onClick={() => resendPaymentLink(booking.id)}
                                className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded"
                                title="Send Payment Link"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => markAsPaid(booking.id)}
                                className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                                title="Mark as Paid"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {booking.status === "pending" && (
                            <button 
                              onClick={() => updateBookingStatus(booking.id, "confirmed")}
                              className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                              title="Confirm"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {booking.status !== "cancelled" && (
                            <button 
                              onClick={() => cancelBooking(booking.id)}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {(booking.payment_status === "paid" || booking.status === "confirmed") && (
                            <>
                              <button
                                onClick={() => downloadReceipt(booking.id)}
                                className="p-1.5 text-[#EA580C] hover:bg-[#EA580C]/20 rounded"
                                title="Download Receipt PDF"
                                data-testid={`receipt-download-${booking.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => resendConfirmation(booking.id)}
                                className="p-1.5 text-purple-400 hover:bg-purple-500/20 rounded"
                                title="Resend Confirmation + Receipt Email"
                                data-testid={`resend-confirmation-${booking.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => sendReviewRequest(booking.id)}
                                className={`p-1.5 rounded ${booking.review_request_sent ? 'text-yellow-400/50 hover:bg-yellow-500/10' : 'text-yellow-400 hover:bg-yellow-500/20'}`}
                                title={booking.review_request_sent ? "Review request already sent — click to resend" : "Send Google Review Request"}
                                data-testid={`review-request-${booking.id}`}
                              >
                                <Star className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button 
                            onClick={() => deleteBooking(booking.id)}
                            className="p-1.5 text-red-600 hover:bg-red-600/20 rounded"
                            title="Delete Permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredBookings.length === 0 && (
                <div className="p-8 text-center text-white/40">
                  No bookings found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="space-y-6" data-testid="calendar-view">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="font-anton text-2xl text-white">Booking Calendar</h2>
                <p className="text-white/40 text-xs font-manrope mt-1">Click any day to view bookings or block bikes</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 text-xs font-manrope">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500/40" /> Available</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#EA580C]" /> Booked</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Blocked</span>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/5"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-anton text-xl text-white">
                  {format(calendarMonth, "MMMM yyyy")}
                </span>
                <button 
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/5"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                </div>
              </div>
            </div>

            <div className="bg-[#171717] border border-white/5 p-4">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <div key={day} className="p-2 text-center text-white/40 text-sm font-manrope">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {eachDayOfInterval({
                  start: startOfMonth(calendarMonth),
                  end: endOfMonth(calendarMonth)
                }).map((date, i) => {
                  const dayOfWeek = date.getDate() === 1 ? (date.getDay() + 6) % 7 : null;
                  return (
                    <>
                      {dayOfWeek !== null && [...Array(dayOfWeek)].map((_, j) => (
                        <div key={`empty-${j}`} className="p-2 min-h-[80px]" />
                      ))}
                      <CalendarDay key={date.toISOString()} date={date} />
                    </>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === "articles" && (
          <div className="space-y-6" data-testid="articles-view">
            <div className="flex items-center justify-between">
              <h2 className="font-anton text-2xl text-white">Articles</h2>
              <button
                onClick={() => openArticleEditor()}
                className="flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 text-sm"
                data-testid="new-article-btn"
              >
                <Plus className="w-4 h-4" />
                New Article
              </button>
            </div>

            <div className="bg-[#171717] border border-white/5 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#0A0A0A]">
                  <tr>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Title</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Status</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Created</th>
                    <th className="text-left p-4 text-white/60 text-sm font-manrope">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article) => (
                    <tr key={article.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="text-white font-semibold">{article.title}</div>
                        <div className="text-white/50 text-sm">/artiklar/{article.slug}</div>
                      </td>
                      <td className="p-4">
                        <button 
                          onClick={() => toggleArticleStatus(article)}
                          className={`px-2 py-1 text-xs uppercase tracking-wider ${
                            article.status === "published" 
                              ? "bg-green-500/20 text-green-400" 
                              : "bg-yellow-500/20 text-yellow-400"
                          }`}
                        >
                          {article.status}
                        </button>
                      </td>
                      <td className="p-4 text-white/60 text-sm">
                        {article.created_at ? format(parseISO(article.created_at), "MMM d, yyyy") : "-"}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => openArticleEditor(article)}
                            className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <a 
                            href={`/artiklar/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                            title="View"
                          >
                            <FileText className="w-4 h-4" />
                          </a>
                          <button 
                            onClick={() => deleteArticle(article.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {articles.length === 0 && (
                <div className="p-8 text-center text-white/40">
                  No articles yet. Create your first article!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6" data-testid="analytics-view">
            <div className="flex items-center justify-between">
              <h2 className="font-anton text-2xl text-white">Analytics</h2>
              <Select 
                value={analyticsPeriod.toString()} 
                onValueChange={(v) => setAnalyticsPeriod(parseInt(v))}
              >
                <SelectTrigger className="w-40 bg-[#171717] border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-white/10">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {analyticsData ? (
              <>
                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#171717] border border-white/5 p-6">
                    <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
                      <MousePointer className="w-4 h-4" />
                      Page Views
                    </div>
                    <div className="font-anton text-3xl text-white">
                      {analyticsData.totals.page_views.toLocaleString()}
                    </div>
                    <div className={`flex items-center gap-1 text-sm mt-1 ${
                      analyticsData.totals.views_trend >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {analyticsData.totals.views_trend >= 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {Math.abs(analyticsData.totals.views_trend)}% vs prev
                    </div>
                  </div>

                  <div className="bg-[#171717] border border-white/5 p-6">
                    <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
                      <Users className="w-4 h-4" />
                      Unique Visitors
                    </div>
                    <div className="font-anton text-3xl text-white">
                      {analyticsData.totals.unique_visitors.toLocaleString()}
                    </div>
                    <div className={`flex items-center gap-1 text-sm mt-1 ${
                      analyticsData.totals.visitors_trend >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {analyticsData.totals.visitors_trend >= 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {Math.abs(analyticsData.totals.visitors_trend)}% vs prev
                    </div>
                  </div>

                  <div className="bg-[#171717] border border-white/5 p-6">
                    <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
                      <MousePointer className="w-4 h-4" />
                      Today's Views
                    </div>
                    <div className="font-anton text-3xl text-white">
                      {analyticsData.today.page_views.toLocaleString()}
                    </div>
                    <div className="text-white/40 text-sm mt-1">
                      {analyticsData.today.unique_visitors} visitors
                    </div>
                  </div>

                  <div className="bg-[#171717] border border-white/5 p-6">
                    <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
                      <BarChart3 className="w-4 h-4" />
                      Avg Daily Views
                    </div>
                    <div className="font-anton text-3xl text-white">
                      {Math.round(analyticsData.totals.page_views / analyticsPeriod).toLocaleString()}
                    </div>
                    <div className="text-white/40 text-sm mt-1">
                      per day
                    </div>
                  </div>
                </div>

                {/* Traffic Chart */}
                <div className="bg-[#171717] border border-white/5 p-6">
                  <h3 className="font-anton text-lg text-white mb-4">Traffic Over Time</h3>
                  <div className="h-48 flex items-end gap-1">
                    {analyticsData.daily_stats.map((day, i) => {
                      const maxViews = Math.max(...analyticsData.daily_stats.map(d => d.page_views), 1);
                      const height = (day.page_views / maxViews) * 100;
                      return (
                        <div 
                          key={i}
                          className="flex-1 bg-[#EA580C]/70 hover:bg-[#EA580C] transition-colors rounded-t cursor-pointer group relative"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day.date}: ${day.page_views} views`}
                        >
                          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                            {day.date}: {day.page_views} views
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>{analyticsData.period.start}</span>
                    <span>{analyticsData.period.end}</span>
                  </div>
                </div>

                {/* Sources, Devices, Pages */}
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Traffic Sources */}
                  <div className="bg-[#171717] border border-white/5 p-6">
                    <h3 className="font-anton text-lg text-white mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Traffic Sources
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analyticsData.sources).length > 0 ? (
                        Object.entries(analyticsData.sources)
                          .sort((a, b) => b[1] - a[1])
                          .map(([source, count]) => {
                            const total = Object.values(analyticsData.sources).reduce((a, b) => a + b, 0);
                            const pct = Math.round((count / total) * 100);
                            return (
                              <div key={source}>
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-white capitalize">{source}</span>
                                  <span className="text-white/60">{pct}%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-[#EA580C]" 
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <p className="text-white/40 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>

                  {/* Devices */}
                  <div className="bg-[#171717] border border-white/5 p-6">
                    <h3 className="font-anton text-lg text-white mb-4 flex items-center gap-2">
                      <Monitor className="w-5 h-5" />
                      Devices
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analyticsData.devices).length > 0 ? (
                        Object.entries(analyticsData.devices)
                          .sort((a, b) => b[1] - a[1])
                          .map(([device, count]) => {
                            const total = Object.values(analyticsData.devices).reduce((a, b) => a + b, 0);
                            const pct = Math.round((count / total) * 100);
                            const Icon = device === 'mobile' ? Smartphone : Monitor;
                            return (
                              <div key={device} className="flex items-center gap-3">
                                <Icon className="w-5 h-5 text-white/50" />
                                <div className="flex-1">
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-white capitalize">{device}</span>
                                    <span className="text-white/60">{count} ({pct}%)</span>
                                  </div>
                                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-blue-500" 
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <p className="text-white/40 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>

                  {/* Top Pages */}
                  <div className="bg-[#171717] border border-white/5 p-6">
                    <h3 className="font-anton text-lg text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Top Pages
                    </h3>
                    <div className="space-y-2">
                      {analyticsData.top_pages.length > 0 ? (
                        analyticsData.top_pages.slice(0, 5).map(([page, count]) => (
                          <div key={page} className="flex justify-between items-center text-sm">
                            <span className="text-white/70 truncate max-w-[150px]" title={page.replace(/_/g, '/')}>
                              {page.replace(/_/g, '/') || '/'}
                            </span>
                            <span className="text-white font-semibold">{count}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-white/40 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Booking Funnel */}
                <div className="bg-[#171717] border border-white/5 p-6">
                  <h3 className="font-anton text-lg text-white mb-4">Booking Funnel</h3>
                  <div className="flex items-center gap-2">
                    {[
                      { label: "Page Views", value: analyticsData.funnel.page_views, color: "bg-blue-500" },
                      { label: "Started Booking", value: analyticsData.funnel.booking_started, color: "bg-yellow-500" },
                      { label: "Step 2", value: analyticsData.funnel.booking_step_2, color: "bg-orange-500" },
                      { label: "Step 3", value: analyticsData.funnel.booking_step_3, color: "bg-[#EA580C]" },
                      { label: "Completed", value: analyticsData.funnel.booking_completed, color: "bg-green-500" }
                    ].map((step, i) => {
                      const maxVal = analyticsData.funnel.page_views || 1;
                      const width = Math.max((step.value / maxVal) * 100, 5);
                      return (
                        <div key={i} className="flex-1 text-center">
                          <div className="h-24 flex items-end justify-center mb-2">
                            <div 
                              className={`w-full ${step.color} rounded-t`}
                              style={{ height: `${width}%` }}
                            />
                          </div>
                          <div className="text-white font-bold">{step.value}</div>
                          <div className="text-white/50 text-xs">{step.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-[#EA580C] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        )}
        {/* Tour Products Tab */}
        {activeTab === "tours" && (
          <div className="space-y-6" data-testid="tours-view">
            <div className="flex items-center justify-between">
              <h2 className="font-anton text-2xl text-white">Tour Products</h2>
              <Button onClick={() => {
                setEditingTour(null);
                setTourForm({ title: "", title_sv: "", description: "", description_sv: "", image: "", gallery: [], date: "", end_date: "", time_start: "09:00", time_end: "17:00", price: "", max_riders: 4, difficulty: "medium", distance: "", route_description: "", route_description_sv: "", included: "", included_sv: "", excluded: "", excluded_sv: "", highlights: "", highlights_sv: "", meeting_point: "Torpvägen 20, 147 63 Uttran, Stockholm", meeting_point_sv: "Torpvägen 20, 147 63 Uttran, Stockholm", status: "draft", country: "sweden", currency: "sek" });
                setShowTourEditor(true);
              }} className="bg-[#EA580C] hover:bg-[#C2410C]" data-testid="add-tour-btn">
                <Plus className="w-4 h-4 mr-2" /> New Tour
              </Button>
            </div>

            {showTourEditor && (
              <div className="bg-[#171717] border border-white/10 p-6 space-y-5 max-h-[80vh] overflow-y-auto">
                <h3 className="font-anton text-lg text-white">{editingTour ? "Edit Tour" : "Create Tour"}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Title (EN)</Label>
                    <Input value={tourForm.title} onChange={(e) => setTourForm({...tourForm, title: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-title" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Title (SV)</Label>
                    <Input value={tourForm.title_sv} onChange={(e) => setTourForm({...tourForm, title_sv: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-title-sv" />
                  </div>
                </div>
                {/* Slug / URL */}
                <div>
                  <Label className="text-white/70 text-sm flex items-center gap-2">
                    URL-slug
                    {tourForm.slug && (
                      <span className="text-white/40 text-xs font-normal font-mono">/turer/{tourForm.slug}</span>
                    )}
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={tourForm.slug || ""}
                      onChange={(e) => {
                        // Normalise as user types: lowercase, replace spaces with -, keep a-z 0-9 -
                        const cleaned = (e.target.value || "")
                          .toLowerCase()
                          .replace(/[åä]/g, 'a').replace(/[ö]/g, 'o').replace(/[é]/g, 'e')
                          .replace(/[^a-z0-9-\s]/g, '')
                          .replace(/\s+/g, '-')
                          .replace(/-+/g, '-');
                        setTourForm({...tourForm, slug: cleaned});
                      }}
                      placeholder="t.ex. grusaventyret"
                      className="bg-[#0A0A0A] border-white/10 text-white flex-1 font-mono text-sm"
                      data-testid="tour-slug"
                    />
                    {!editingTour && (
                      <Button
                        type="button"
                        onClick={() => {
                          const src = tourForm.title || tourForm.title_sv || "";
                          const cleaned = src.toLowerCase()
                            .replace(/[åä]/g, 'a').replace(/[ö]/g, 'o').replace(/[é]/g, 'e')
                            .replace(/[^a-z0-9-\s]/g, '')
                            .trim().replace(/\s+/g, '-');
                          setTourForm({...tourForm, slug: cleaned});
                        }}
                        className="bg-[#0A0A0A] border border-white/10 hover:bg-[#171717] text-white text-xs px-3"
                        data-testid="tour-slug-from-title"
                      >Från titel</Button>
                    )}
                  </div>
                  <p className="text-white/40 text-xs mt-1">
                    {editingTour
                      ? "Ändra för att uppdatera URL:en. Gamla länkar slutar fungera om du byter slug."
                      : "Lämna tomt för att auto-generera från titel. Använd a-z, 0-9 och bindestreck."}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Startdatum</Label>
                    <Input type="date" value={tourForm.date} onChange={(e) => setTourForm({...tourForm, date: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-date" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Slutdatum (valfritt)</Label>
                    <Input type="date" value={tourForm.end_date || ""} onChange={(e) => setTourForm({...tourForm, end_date: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-end-date" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Price (SEK)</Label>
                    <Input type="number" value={tourForm.price} onChange={(e) => setTourForm({...tourForm, price: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-price" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Max Riders</Label>
                    <Input type="number" value={tourForm.max_riders} onChange={(e) => setTourForm({...tourForm, max_riders: parseInt(e.target.value) || 4})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="tour-max-riders" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Difficulty</Label>
                    <Select value={tourForm.difficulty} onValueChange={(v) => setTourForm({...tourForm, difficulty: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-white/10">
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Start Time</Label>
                    <Input value={tourForm.time_start} onChange={(e) => setTourForm({...tourForm, time_start: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">End Time</Label>
                    <Input value={tourForm.time_end} onChange={(e) => setTourForm({...tourForm, time_end: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Distance</Label>
                    <Input value={tourForm.distance} onChange={(e) => setTourForm({...tourForm, distance: e.target.value})} placeholder="e.g. 180 km" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Status</Label>
                    <Select value={tourForm.status} onValueChange={(v) => setTourForm({...tourForm, status: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-white/10">
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="sold_out">Sold Out</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Country</Label>
                    <Select value={tourForm.country || "sweden"} onValueChange={(v) => setTourForm({...tourForm, country: v, currency: v === 'iceland' ? 'eur' : 'sek'})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-white/10">
                        <SelectItem value="sweden">Sweden (SEK)</SelectItem>
                        <SelectItem value="iceland">Iceland (EUR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <ImageUploadField
                    value={tourForm.image}
                    onChange={(v) => setTourForm({...tourForm, image: v})}
                    label="Tour image"
                    testId="tour-image"
                    authHeader={authHeader}
                  />
                  
                  {/* Gallery (extra images) */}
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-white/70 text-sm">Galleri (extra bilder)</Label>
                      <span className="text-white/40 text-xs">{(tourForm.gallery || []).length} bilder</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                      {(tourForm.gallery || []).map((img, idx) => (
                        <div key={idx} className="relative group border border-white/10" data-testid={`gallery-item-${idx}`}>
                          <img src={convertDropboxUrl(img)} alt={`Gallery ${idx+1}`} className="w-full h-24 object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            {idx > 0 && (
                              <button type="button" onClick={() => {
                                const g = [...tourForm.gallery]; [g[idx-1], g[idx]] = [g[idx], g[idx-1]];
                                setTourForm({...tourForm, gallery: g});
                              }} className="text-white text-xs px-2 py-0.5 bg-white/10 hover:bg-white/20" data-testid={`gallery-move-up-${idx}`}>← Flytta</button>
                            )}
                            <button type="button" onClick={() => {
                              setTourForm({...tourForm, gallery: tourForm.gallery.filter((_, i) => i !== idx)});
                            }} className="text-red-400 text-xs px-2 py-0.5 bg-white/10 hover:bg-red-500/30" data-testid={`gallery-remove-${idx}`}>Ta bort</button>
                          </div>
                          <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5">{idx+1}</span>
                        </div>
                      ))}
                    </div>
                    <ImageUploadField
                      value=""
                      onChange={(url) => {
                        if (url) setTourForm({...tourForm, gallery: [...(tourForm.gallery || []), url]});
                      }}
                      label="Lägg till bild i galleriet"
                      testId="gallery-add"
                      authHeader={authHeader}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Description (EN)</Label>
                    <textarea value={tourForm.description} onChange={(e) => setTourForm({...tourForm, description: e.target.value})} rows={4} className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 text-sm" data-testid="tour-description" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Description (SV)</Label>
                    <textarea value={tourForm.description_sv} onChange={(e) => setTourForm({...tourForm, description_sv: e.target.value})} rows={4} className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Route Description (EN)</Label>
                    <textarea value={tourForm.route_description} onChange={(e) => setTourForm({...tourForm, route_description: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Route Description (SV)</Label>
                    <textarea value={tourForm.route_description_sv} onChange={(e) => setTourForm({...tourForm, route_description_sv: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 mt-1 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Included (EN, comma-separated)</Label>
                    <Input value={tourForm.included} onChange={(e) => setTourForm({...tourForm, included: e.target.value})} placeholder="Bike, Gear, Guide, Fuel" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Included (SV, comma-separated)</Label>
                    <Input value={tourForm.included_sv} onChange={(e) => setTourForm({...tourForm, included_sv: e.target.value})} placeholder="MC, Utrustning, Guide, Bränsle" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Excluded (EN, comma-separated)</Label>
                    <Input value={tourForm.excluded} onChange={(e) => setTourForm({...tourForm, excluded: e.target.value})} placeholder="Lunch, Personal insurance" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Excluded (SV, comma-separated)</Label>
                    <Input value={tourForm.excluded_sv} onChange={(e) => setTourForm({...tourForm, excluded_sv: e.target.value})} placeholder="Lunch, Personlig försäkring" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Highlights (EN, comma-separated)</Label>
                    <Input value={tourForm.highlights} onChange={(e) => setTourForm({...tourForm, highlights: e.target.value})} placeholder="Scenic routes, Hidden waterfalls" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Highlights (SV, comma-separated)</Label>
                    <Input value={tourForm.highlights_sv} onChange={(e) => setTourForm({...tourForm, highlights_sv: e.target.value})} placeholder="Natursköna vägar, Gömda vattenfall" className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Meeting Point (EN)</Label>
                    <Input value={tourForm.meeting_point} onChange={(e) => setTourForm({...tourForm, meeting_point: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Meeting Point (SV)</Label>
                    <Input value={tourForm.meeting_point_sv} onChange={(e) => setTourForm({...tourForm, meeting_point_sv: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                  </div>
                  
                  {/* Variants (paket / attribut) */}
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-white/70 text-sm">Varianter / paket (valfritt)</Label>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          const newVariant = { id: (crypto?.randomUUID && crypto.randomUUID()) || `v-${Date.now()}`, name: "", name_sv: "", price: tourForm.price || 0, max_riders: 4, booked_riders: 0 };
                          setTourForm({ ...tourForm, variants: [...(tourForm.variants || []), newVariant] });
                        }}
                        className="bg-[#EA580C] hover:bg-[#C2410C] text-xs"
                        data-testid="add-variant-btn"
                      >+ Lägg till variant</Button>
                    </div>
                    <p className="text-white/40 text-xs mb-3">
                      Om en tur har varianter (t.ex. &quot;Med MC från oss&quot; och &quot;Med egen MC&quot;) visas en val-knapp på turens sida och kunden måste välja innan bokning. Pris och tillgänglighet hanteras per variant. Lämna tomt om turen bara har ett alternativ.
                    </p>
                    <div className="space-y-2">
                      {(tourForm.variants || []).map((v, idx) => (
                        <div key={v.id || idx} className="bg-[#0A0A0A] border border-white/10 p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`variant-row-${idx}`}>
                          <Input
                            placeholder="Namn EN (e.g. Bring your own bike)"
                            value={v.name || ""}
                            onChange={(e) => {
                              const arr = [...tourForm.variants]; arr[idx] = { ...arr[idx], name: e.target.value };
                              setTourForm({ ...tourForm, variants: arr });
                            }}
                            className="bg-[#171717] border-white/10 text-white text-xs sm:col-span-3"
                            data-testid={`variant-name-${idx}`}
                          />
                          <Input
                            placeholder="Namn SV (t.ex. Med egen MC)"
                            value={v.name_sv || ""}
                            onChange={(e) => {
                              const arr = [...tourForm.variants]; arr[idx] = { ...arr[idx], name_sv: e.target.value };
                              setTourForm({ ...tourForm, variants: arr });
                            }}
                            className="bg-[#171717] border-white/10 text-white text-xs sm:col-span-3"
                            data-testid={`variant-name-sv-${idx}`}
                          />
                          <Input
                            type="number"
                            placeholder="Pris"
                            value={v.price}
                            onChange={(e) => {
                              const arr = [...tourForm.variants]; arr[idx] = { ...arr[idx], price: parseFloat(e.target.value) || 0 };
                              setTourForm({ ...tourForm, variants: arr });
                            }}
                            className="bg-[#171717] border-white/10 text-white text-xs sm:col-span-2"
                            data-testid={`variant-price-${idx}`}
                          />
                          <Input
                            type="number"
                            placeholder="Max förare"
                            value={v.max_riders}
                            onChange={(e) => {
                              const arr = [...tourForm.variants]; arr[idx] = { ...arr[idx], max_riders: parseInt(e.target.value) || 0 };
                              setTourForm({ ...tourForm, variants: arr });
                            }}
                            className="bg-[#171717] border-white/10 text-white text-xs sm:col-span-2"
                            data-testid={`variant-max-${idx}`}
                          />
                          <div className="text-white/40 text-[10px] sm:col-span-1">
                            Bokade: {v.booked_riders || 0}
                          </div>
                          <button
                            type="button"
                            onClick={() => setTourForm({ ...tourForm, variants: tourForm.variants.filter((_, i) => i !== idx) })}
                            className="text-red-400 hover:text-red-300 text-xs sm:col-span-1 justify-self-end"
                            data-testid={`variant-remove-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button onClick={async () => {
                    if (!tourForm.title || !tourForm.date || !tourForm.price) { toast.error("Fill in title, date and price"); return; }
                    const splitList = (s) => s ? s.split(",").map(i => i.trim()).filter(Boolean) : [];
                    const payload = {
                      ...tourForm,
                      price: parseFloat(tourForm.price),
                      included: splitList(tourForm.included), included_sv: splitList(tourForm.included_sv),
                      excluded: splitList(tourForm.excluded), excluded_sv: splitList(tourForm.excluded_sv),
                      highlights: splitList(tourForm.highlights), highlights_sv: splitList(tourForm.highlights_sv),
                    };
                    try {
                      if (editingTour) {
                        await axios.put(`${API}/admin/tour-products/${editingTour.id}`, payload, { headers: { Authorization: authHeader } });
                        toast.success("Tour updated");
                      } else {
                        await axios.post(`${API}/admin/tour-products`, payload, { headers: { Authorization: authHeader } });
                        toast.success("Tour created");
                      }
                      setShowTourEditor(false);
                      fetchTourProducts();
                    } catch (e) { toast.error("Failed to save tour"); }
                  }} className="bg-[#EA580C] hover:bg-[#C2410C]" data-testid="save-tour-btn">{editingTour ? "Update" : "Create"} Tour</Button>
                  <Button variant="outline" onClick={() => setShowTourEditor(false)} className="border-white/10 text-white">Cancel</Button>
                </div>
              </div>
            )}

            {/* Tour list */}
            <div className="grid gap-4">
              {tourProducts.map((tour) => (
                <div key={tour.id} className="bg-[#171717] border border-white/10 p-5 flex gap-5" data-testid={`tour-${tour.id}`}>
                  {tour.image && <img src={convertDropboxUrl(tour.image)} alt={tour.title} className="w-32 h-24 object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-anton text-lg text-white">{tour.title}</h3>
                        {tour.title_sv && <p className="text-white/40 text-xs">{tour.title_sv}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs uppercase font-bold flex-shrink-0 ${
                          tour.country === 'iceland' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>{tour.country || 'sweden'}</span>
                        <span className={`px-2 py-1 text-xs uppercase font-bold flex-shrink-0 ${
                          tour.status === 'published' ? 'bg-green-500/20 text-green-400' :
                          tour.status === 'sold_out' ? 'bg-red-500/20 text-red-400' :
                          tour.status === 'completed' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>{tour.status}</span>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-sm text-white/60">
                      <span>{tour.date?.slice(0, 10)}</span>
                      <span>{tour.time_start} - {tour.time_end}</span>
                      <span className="text-[#EA580C] font-bold">{tour.currency === 'eur' ? `€${Number(tour.price).toLocaleString()}` : `${Number(tour.price).toLocaleString('sv-SE')} kr`}</span>
                      <span>{tour.booked_riders || 0}/{tour.max_riders} riders</span>
                      {tour.difficulty && <span className={`${tour.difficulty === 'easy' ? 'text-green-400' : tour.difficulty === 'hard' ? 'text-red-400' : 'text-yellow-400'}`}>{tour.difficulty}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 items-start">
                    <button onClick={() => {
                      setEditingTour(tour);
                      setTourForm({
                        ...tour,
                        price: tour.price?.toString() || "",
                        included: (tour.included || []).join(", "),
                        included_sv: (tour.included_sv || []).join(", "),
                        excluded: (tour.excluded || []).join(", "),
                        excluded_sv: (tour.excluded_sv || []).join(", "),
                        highlights: (tour.highlights || []).join(", "),
                        highlights_sv: (tour.highlights_sv || []).join(", "),
                      });
                      setShowTourEditor(true);
                    }} className="text-white/50 hover:text-white"><Edit className="w-4 h-4" /></button>
                    <button onClick={async () => {
                      if (!window.confirm("Delete this tour?")) return;
                      try { await axios.delete(`${API}/admin/tour-products/${tour.id}`, { headers: { Authorization: authHeader } }); toast.success("Tour deleted"); fetchTourProducts(); } catch (e) { toast.error("Failed"); }
                    }} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {tourProducts.length === 0 && !showTourEditor && (
                <div className="bg-[#171717] border border-white/10 p-12 text-center text-white/40">
                  <Mountain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No tour products yet. Create your first tour above!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Motorcycle Fleet Tab */}
        {activeTab === "fleet" && (
          <div className="space-y-6" data-testid="fleet-view">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-anton text-2xl text-white">MC-flotta</h2>
                <p className="text-white/50 text-sm mt-1">Hantera dina motorcyklar. Varje MC har ett unikt registreringsnummer och kan kopplas till bokningar/avtal.</p>
              </div>
              <button
                onClick={openNewMotorcycle}
                className="flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 text-sm"
                data-testid="new-motorcycle-btn"
              >
                <Plus className="w-4 h-4" />
                Lagg till MC
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* MC list */}
              <div className="lg:col-span-2 bg-[#171717] border border-white/10">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs text-white/50 uppercase">
                      <th className="p-3">Reg.nr</th>
                      <th className="p-3">Namn</th>
                      <th className="p-3">Modell</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Atgard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {motorcycles.map((mc) => (
                      <tr
                        key={mc.id}
                        className={`border-b border-white/5 text-sm cursor-pointer hover:bg-white/5 ${mcSchedule?.motorcycle?.id === mc.id ? "bg-white/5" : ""}`}
                        onClick={() => loadMcSchedule(mc)}
                        data-testid={`motorcycle-row-${mc.registration.replace(/\s+/g, '-')}`}
                      >
                        <td className="p-3">
                          <span className="font-mono font-bold text-[#a3a63c] tracking-wider">{mc.registration}</span>
                        </td>
                        <td className="p-3 text-white/80">{mc.name || <span className="text-white/30">-</span>}</td>
                        <td className="p-3 text-white/80">{mc.model || <span className="text-white/30">-</span>}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 text-xs ${
                            mc.status === "active" ? "bg-green-500/20 text-green-400"
                            : mc.status === "in_service" ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-white/10 text-white/50"
                          }`}>
                            {mc.status === "active" ? "Aktiv" : mc.status === "in_service" ? "Service" : "Pensionerad"}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => openEditMotorcycle(mc)}
                              className="p-2 hover:bg-white/10 text-white/70"
                              title="Redigera"
                              data-testid={`edit-motorcycle-${mc.registration.replace(/\s+/g, '-')}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteMotorcycle(mc.id)}
                              className="p-2 hover:bg-red-500/20 text-white/70 hover:text-red-400"
                              title="Ta bort"
                              data-testid={`delete-motorcycle-${mc.registration.replace(/\s+/g, '-')}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {motorcycles.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-white/40">
                          <Bike className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          Inga MC i flottan an. Klicka pa Lagg till MC ovan.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* MC schedule panel */}
              <div className="bg-[#171717] border border-white/10 p-4" data-testid="mc-schedule-panel">
                {mcSchedule ? (
                  <div>
                    <div className="mb-3 pb-3 border-b border-white/10">
                      <div className="text-white/50 text-xs uppercase">Schema for</div>
                      <div className="font-mono text-lg font-bold text-[#a3a63c] tracking-wider">
                        {mcSchedule.motorcycle.registration}
                      </div>
                      {mcSchedule.motorcycle.name && (
                        <div className="text-white/70 text-sm">{mcSchedule.motorcycle.name}</div>
                      )}
                    </div>

                    <div className="mb-4">
                      <div className="text-white/50 text-xs uppercase mb-2">Bokningar ({mcSchedule.bookings.length})</div>
                      {mcSchedule.bookings.length === 0 ? (
                        <div className="text-white/30 text-sm italic">Inga bokningar</div>
                      ) : (
                        <div className="space-y-2">
                          {mcSchedule.bookings.slice(0, 20).map((b) => (
                            <div key={b.id} className="bg-black/40 border border-white/5 p-2 text-xs">
                              <div className="flex justify-between text-white/80">
                                <span>{(b.date || "").slice(0, 10)}{b.end_date ? ` - ${b.end_date.slice(0, 10)}` : ""}</span>
                                <span className={`px-1.5 py-0.5 ${
                                  b.status === "agreement_signed" ? "bg-green-500/20 text-green-400" :
                                  b.status === "confirmed" ? "bg-blue-500/20 text-blue-400" :
                                  "bg-yellow-500/20 text-yellow-400"
                                }`}>{b.status}</span>
                              </div>
                              <div className="text-white/60 mt-1">{b.customer_name} — {b.package_name}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-white/50 text-xs uppercase mb-2">Vantande avtal ({mcSchedule.pending_agreements.length})</div>
                      {mcSchedule.pending_agreements.length === 0 ? (
                        <div className="text-white/30 text-sm italic">Inga vantande avtal</div>
                      ) : (
                        <div className="space-y-2">
                          {mcSchedule.pending_agreements.map((a) => (
                            <div key={a.id} className="bg-black/40 border border-yellow-500/20 p-2 text-xs">
                              <div className="text-white/80">{(a.date || "").slice(0, 10)}{a.end_date ? ` - ${a.end_date.slice(0, 10)}` : ""}</div>
                              <div className="text-white/60 mt-1">{a.package_name || "Avtal"} · {Number(a.amount).toLocaleString("sv-SE")} kr</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-white/40 py-8 text-sm">
                    <Bike className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Klicka pa en MC for att se dess schema
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* MC modal */}
        {showMotorcycleModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="motorcycle-modal">
            <div className="bg-[#171717] border border-white/10 max-w-md w-full">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-anton text-xl text-white">
                  {editingMotorcycle ? "Redigera MC" : "Lagg till MC"}
                </h3>
                <button
                  onClick={() => setShowMotorcycleModal(false)}
                  className="text-white/50 hover:text-white"
                  data-testid="close-motorcycle-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <Label className="text-white/70 text-xs">Registreringsnummer *</Label>
                  <Input
                    value={mcForm.registration}
                    onChange={(e) => setMcForm({ ...mcForm, registration: e.target.value.toUpperCase() })}
                    placeholder="T.ex. ABC 123"
                    className="bg-black/40 border-white/10 text-white mt-1 uppercase font-mono"
                    data-testid="motorcycle-registration-input"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Namn / smeknamn (frivilligt)</Label>
                  <Input
                    value={mcForm.name}
                    onChange={(e) => setMcForm({ ...mcForm, name: e.target.value })}
                    placeholder="T.ex. Rulle"
                    className="bg-black/40 border-white/10 text-white mt-1"
                    data-testid="motorcycle-name-input"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Modell (frivilligt)</Label>
                  <Input
                    value={mcForm.model}
                    onChange={(e) => setMcForm({ ...mcForm, model: e.target.value })}
                    placeholder="T.ex. KTM 890 Adventure"
                    className="bg-black/40 border-white/10 text-white mt-1"
                    data-testid="motorcycle-model-input"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Status</Label>
                  <Select
                    value={mcForm.status}
                    onValueChange={(v) => setMcForm({ ...mcForm, status: v })}
                  >
                    <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="motorcycle-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="in_service">Service</SelectItem>
                      <SelectItem value="retired">Pensionerad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Anteckningar</Label>
                  <Input
                    value={mcForm.notes}
                    onChange={(e) => setMcForm({ ...mcForm, notes: e.target.value })}
                    placeholder="Kommentarer, servicehistorik..."
                    className="bg-black/40 border-white/10 text-white mt-1"
                    data-testid="motorcycle-notes-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={saveMotorcycle}
                    className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white py-2 text-sm font-semibold"
                    data-testid="save-motorcycle-btn"
                  >
                    {editingMotorcycle ? "Spara andringar" : "Lagg till"}
                  </button>
                  <button
                    onClick={() => setShowMotorcycleModal(false)}
                    className="px-6 bg-white/10 hover:bg-white/20 text-white py-2 text-sm"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rental Agreements Tab */}
        {activeTab === "agreements" && (
          <div className="space-y-6" data-testid="agreements-view">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-anton text-2xl text-white">Digitala hyresavtal</h2>
                <p className="text-white/50 text-sm mt-1">Skicka en signerings-lank till kunden. Nar avtalet ar signerat skapas en bokning automatiskt (status: agreement_signed) och PDF mailas till bada parter.</p>
              </div>
              <button
                onClick={() => {
                  setCreatedAgreementLink(null);
                  setShowAgreementModal(true);
                  if (motorcycles.length === 0) fetchMotorcycles();
                }}
                className="flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 text-sm"
                data-testid="new-agreement-btn"
              >
                <Plus className="w-4 h-4" />
                Nytt avtal
              </button>
            </div>

            <div className="bg-[#171717] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs text-white/50 uppercase">
                    <th className="p-3">Skapad</th>
                    <th className="p-3">Hyresobjekt</th>
                    <th className="p-3">Datum</th>
                    <th className="p-3">Belopp</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Forare</th>
                    <th className="p-3 text-right">Atgard</th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((a) => {
                    const signUrl = `${window.location.origin}/sign-agreement/${a.id}`;
                    return (
                      <tr key={a.id} className="border-b border-white/5 text-sm" data-testid={`agreement-row-${a.id}`}>
                        <td className="p-3 text-white/70">{(a.created_at || "").slice(0, 10)}</td>
                        <td className="p-3 text-white">{a.package_name || a.variant_name || "Rental"}</td>
                        <td className="p-3 text-white/70">
                          {(a.date || "").slice(0, 10)}
                          {a.end_date ? ` - ${a.end_date.slice(0, 10)}` : ""}
                        </td>
                        <td className="p-3 text-white">{Number(a.amount).toLocaleString("sv-SE")} kr</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 text-xs ${
                            a.status === "signed"
                              ? "bg-green-500/20 text-green-400"
                              : a.status === "cancelled"
                              ? "bg-white/10 text-white/50"
                              : "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {a.status === "signed" ? "Signerat" : a.status === "cancelled" ? "Avbrutet" : "Vantar signering"}
                          </span>
                        </td>
                        <td className="p-3 text-white/70 text-xs">
                          {a.driver_name ? (
                            <>
                              {a.driver_name}
                              <br />
                              <span className="text-white/40">{a.driver_email}</span>
                            </>
                          ) : (
                            <span className="text-white/30">-</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            {a.status !== "signed" && (
                              <button
                                onClick={() => copyToClipboard(signUrl)}
                                className="p-2 hover:bg-white/10 text-white/70"
                                title="Kopiera signeringslank"
                                data-testid={`copy-agreement-${a.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                            {a.status === "signed" && (
                              <button
                                onClick={() => downloadAgreementPdf(a.id)}
                                className="p-2 hover:bg-white/10 text-white/70"
                                title="Ladda ner PDF"
                                data-testid={`download-agreement-${a.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            {a.status !== "signed" && (
                              <button
                                onClick={() => deleteAgreement(a.id)}
                                className="p-2 hover:bg-red-500/20 text-white/70 hover:text-red-400"
                                title="Ta bort"
                                data-testid={`delete-agreement-${a.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {agreements.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-white/40">
                        <ClipboardSignature className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        Inga avtal an. Skapa ett nytt avtal ovan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* New Agreement Modal */}
        {showAgreementModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="agreement-modal">
            <div className="bg-[#171717] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#171717]">
                <h3 className="font-anton text-xl text-white">
                  {createdAgreementLink ? "Avtal skapat" : "Nytt hyresavtal"}
                </h3>
                <button
                  onClick={() => {
                    setShowAgreementModal(false);
                    setCreatedAgreementLink(null);
                    setAgreementForm({
                      package_id: "half_day",
                      package_name: "",
                      motorcycle_registration: "",
                      date: "",
                      end_date: "",
                      time_slot: "",
                      riders: 1,
                      amount: "",
                      language: "sv",
                      prefill_email: "",
                      admin_notes: "",
                    });
                  }}
                  className="text-white/50 hover:text-white"
                  data-testid="close-agreement-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {createdAgreementLink ? (
                <div className="p-6 space-y-4">
                  <div className="bg-green-500/10 border border-green-500/30 p-4 text-green-400 text-sm">
                    Avtalet ar skapat. Kopiera lanken nedan och skicka till kunden. Nar kunden signerar skapas bokningen automatiskt.
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs uppercase mb-2 block">Signeringslank</Label>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={createdAgreementLink}
                        className="bg-black/40 border-white/10 text-white flex-1"
                        data-testid="created-agreement-link"
                      />
                      <button
                        onClick={() => copyToClipboard(createdAgreementLink)}
                        className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4"
                        data-testid="copy-created-link-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAgreementModal(false);
                      setCreatedAgreementLink(null);
                    }}
                    className="w-full bg-white/10 hover:bg-white/20 text-white py-2"
                  >
                    Stang
                  </button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/70 text-xs">Sprak</Label>
                      <Select
                        value={agreementForm.language}
                        onValueChange={(v) => setAgreementForm({ ...agreementForm, language: v })}
                      >
                        <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="agreement-language-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sv">Svenska</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Paket</Label>
                      <Select
                        value={agreementForm.package_id}
                        onValueChange={(v) => setAgreementForm({ ...agreementForm, package_id: v })}
                      >
                        <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="agreement-package-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="half_day">Halvdag</SelectItem>
                          <SelectItem value="full_day">Heldag</SelectItem>
                          <SelectItem value="guided_tour">Guidad tur</SelectItem>
                          <SelectItem value="custom">Custom (skriv namn nedan)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/70 text-xs">Hyresobjekt / namn (visas i avtal)</Label>
                    <Input
                      value={agreementForm.package_name}
                      onChange={(e) => setAgreementForm({ ...agreementForm, package_name: e.target.value })}
                      placeholder="T.ex. Halvdags MC-uthyrning eller Custom package"
                      className="bg-black/40 border-white/10 text-white mt-1"
                      data-testid="agreement-package-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-white/70 text-xs">Registreringsnummer MC (valfritt)</Label>
                    <Select
                      value={agreementForm.motorcycle_registration || "none"}
                      onValueChange={(v) => setAgreementForm({ ...agreementForm, motorcycle_registration: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1 font-mono" data-testid="agreement-registration-select">
                        <SelectValue placeholder="Valj MC..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">- Ingen specifik MC -</SelectItem>
                        {motorcycles.filter(m => m.status === "active").map((m) => (
                          <SelectItem key={m.id} value={m.registration}>
                            {m.registration}{m.name ? ` — ${m.name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/70 text-xs">Startdatum *</Label>
                      <Input
                        type="date"
                        value={agreementForm.date}
                        onChange={(e) => setAgreementForm({ ...agreementForm, date: e.target.value })}
                        className="bg-black/40 border-white/10 text-white mt-1"
                        data-testid="agreement-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Slutdatum (frivilligt)</Label>
                      <Input
                        type="date"
                        value={agreementForm.end_date}
                        onChange={(e) => setAgreementForm({ ...agreementForm, end_date: e.target.value })}
                        className="bg-black/40 border-white/10 text-white mt-1"
                        data-testid="agreement-end-date-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-white/70 text-xs">Tidsslot (halvdag)</Label>
                      <Select
                        value={agreementForm.time_slot || "none"}
                        onValueChange={(v) => setAgreementForm({ ...agreementForm, time_slot: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="agreement-time-slot-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-</SelectItem>
                          <SelectItem value="morning">Formiddag (09-14)</SelectItem>
                          <SelectItem value="afternoon">Eftermiddag (14-19)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Antal forare</Label>
                      <Input
                        type="number"
                        min="1"
                        value={agreementForm.riders}
                        onChange={(e) => setAgreementForm({ ...agreementForm, riders: e.target.value })}
                        className="bg-black/40 border-white/10 text-white mt-1"
                        data-testid="agreement-riders-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Belopp (SEK) *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={agreementForm.amount}
                        onChange={(e) => setAgreementForm({ ...agreementForm, amount: e.target.value })}
                        placeholder="3500"
                        className="bg-black/40 border-white/10 text-white mt-1"
                        data-testid="agreement-amount-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/70 text-xs">Kundens e-post (frivilligt, forfyller signeringsformularet)</Label>
                    <Input
                      type="email"
                      value={agreementForm.prefill_email}
                      onChange={(e) => setAgreementForm({ ...agreementForm, prefill_email: e.target.value })}
                      placeholder="kund@exempel.se"
                      className="bg-black/40 border-white/10 text-white mt-1"
                      data-testid="agreement-prefill-email-input"
                    />
                  </div>

                  <div>
                    <Label className="text-white/70 text-xs">Interna anteckningar</Label>
                    <Input
                      value={agreementForm.admin_notes}
                      onChange={(e) => setAgreementForm({ ...agreementForm, admin_notes: e.target.value })}
                      placeholder="Faktureras via Fortnox etc."
                      className="bg-black/40 border-white/10 text-white mt-1"
                      data-testid="agreement-notes-input"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={createAgreement}
                      className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white py-2 text-sm font-semibold"
                      data-testid="submit-agreement-btn"
                    >
                      Skapa & generera lank
                    </button>
                    <button
                      onClick={() => setShowAgreementModal(false)}
                      className="px-6 bg-white/10 hover:bg-white/20 text-white py-2 text-sm"
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
